import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { createWantedAd, getCategoriesFlat } from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Info } from 'lucide-react';
import { toast } from 'sonner';

const UNITS = ['kg', 'sqm', 'rolls', 'pcs', 'lm', 'pallet'];

/**
 * Reusable dialog for posting a wanted ad. Handles its own category loading
 * and redirects to /login when the user is not authenticated.
 */
export function PostWantedAdDialog({ open, onClose, onCreated, initialCategories = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState(initialCategories || []);
  const [form, setForm] = useState({
    category_node_id: '',
    material_type_description: '',
    width_min_mm: '',
    width_max_mm: '',
    min_quantity: '',
    quantity_unit: 'kg',
    price_min_eur: '',
    price_max_eur: '',
    valid_until: '',
  });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const loadCategories = useCallback(async () => {
    if (categories.length > 0) return;
    try {
      const res = await getCategoriesFlat();
      setCategories(res.data?.categories || res.data || []);
    } catch (e) { console.error(e); }
  }, [categories.length]);

  useEffect(() => {
    if (open) {
      loadCategories();
      if (!form.valid_until) {
        const d = new Date();
        d.setDate(d.getDate() + 60);
        set('valid_until', d.toISOString().slice(0, 10));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (initialCategories && initialCategories.length) setCategories(initialCategories);
  }, [initialCategories]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Bitte zuerst anmelden.');
      onClose?.();
      navigate('/login');
      return;
    }
    if (!form.category_node_id || !form.material_type_description || !form.min_quantity || !form.valid_until) {
      toast.error('Bitte alle Pflichtfelder ausfüllen.');
      return;
    }
    if (form.price_min_eur && form.price_max_eur &&
        parseFloat(form.price_min_eur) > parseFloat(form.price_max_eur)) {
      toast.error('Min-Preis darf nicht größer als Max-Preis sein.');
      return;
    }
    setLoading(true);
    try {
      await createWantedAd({
        category_node_id: form.category_node_id,
        material_type_description: form.material_type_description,
        width_min_mm: form.width_min_mm ? parseFloat(form.width_min_mm) : null,
        width_max_mm: form.width_max_mm ? parseFloat(form.width_max_mm) : null,
        min_quantity: parseFloat(form.min_quantity),
        quantity_unit: form.quantity_unit,
        price_min_eur: form.price_min_eur ? parseFloat(form.price_min_eur) : null,
        price_max_eur: form.price_max_eur ? parseFloat(form.price_max_eur) : null,
        valid_until: form.valid_until,
      });
      toast.success('Gesuch eingestellt! Passende Anbieter werden benachrichtigt.');
      setForm({
        category_node_id: '', material_type_description: '',
        width_min_mm: '', width_max_mm: '',
        min_quantity: '', quantity_unit: 'kg',
        price_min_eur: '', price_max_eur: '',
        valid_until: '',
      });
      onCreated?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Einstellen des Gesuchs.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="post-wanted-ad-dialog">
        <DialogHeader>
          <DialogTitle>Gesuch einstellen</DialogTitle>
          <DialogDescription>
            Beschreiben Sie, welches Material Sie suchen. Ihre Identität bleibt anonym — nur Land und PLZ werden angezeigt.
            Passende Anbieter erhalten automatisch eine E-Mail.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label>Kategorie <span className="text-red-500">*</span></Label>
            <Select value={form.category_node_id} onValueChange={(v) => set('category_node_id', v)}>
              <SelectTrigger data-testid="wanted-category-select">
                <SelectValue placeholder="Kategorie wählen…" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.node_id} value={c.node_id}>{c.node_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Materialbeschreibung <span className="text-red-500">*</span></Label>
            <Input
              placeholder="z. B. Polyethylen-Folie, transparent, lebensmittelecht"
              value={form.material_type_description}
              onChange={(e) => set('material_type_description', e.target.value)}
              data-testid="wanted-description-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label>
              Breitenbereich (mm)
              <span className="text-muted-foreground text-xs ml-1">optional, ±10% Toleranz</span>
            </Label>
            <div className="flex gap-3">
              <Input
                type="number" placeholder="Min. mm"
                value={form.width_min_mm}
                onChange={(e) => set('width_min_mm', e.target.value)}
                className="flex-1"
                data-testid="wanted-width-min"
              />
              <span className="self-center text-muted-foreground">–</span>
              <Input
                type="number" placeholder="Max. mm"
                value={form.width_max_mm}
                onChange={(e) => set('width_max_mm', e.target.value)}
                className="flex-1"
                data-testid="wanted-width-max"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Mindestmenge <span className="text-red-500">*</span></Label>
            <div className="flex gap-3">
              <Input
                type="number" placeholder="Menge"
                value={form.min_quantity}
                onChange={(e) => set('min_quantity', e.target.value)}
                className="flex-1"
                data-testid="wanted-quantity-input"
              />
              <Select value={form.quantity_unit} onValueChange={(v) => set('quantity_unit', v)}>
                <SelectTrigger className="w-28" data-testid="wanted-unit-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>
              Preisvorstellung (EUR / Einheit)
              <span className="text-muted-foreground text-xs ml-1">optional</span>
            </Label>
            <div className="flex gap-3">
              <Input
                type="number" step="0.01" min="0" placeholder="Min. €"
                value={form.price_min_eur}
                onChange={(e) => set('price_min_eur', e.target.value)}
                className="flex-1"
                data-testid="wanted-price-min"
              />
              <span className="self-center text-muted-foreground">–</span>
              <Input
                type="number" step="0.01" min="0" placeholder="Max. €"
                value={form.price_max_eur}
                onChange={(e) => set('price_max_eur', e.target.value)}
                className="flex-1"
                data-testid="wanted-price-max"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Gültig bis <span className="text-red-500">*</span></Label>
            <Input
              type="date"
              value={form.valid_until}
              onChange={(e) => set('valid_until', e.target.value)}
              min={new Date().toISOString().slice(0, 10)}
              data-testid="wanted-valid-until-input"
            />
          </div>

          <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-3 text-sm text-amber-800 dark:text-amber-300">
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Bei Transaktionen über Gesuche trägt der <strong>Käufer</strong> die Provision.</span>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>Abbrechen</Button>
            <Button
              type="submit"
              className="bg-orange-500 hover:bg-orange-600"
              disabled={loading}
              data-testid="post-wanted-ad-submit"
            >
              {loading ? 'Wird eingestellt…' : 'Gesuch einstellen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
